import React, { useContext, useState, useCallback, useEffect } from 'react';
import { useRecordType } from '../contexts/recordTypeContext';
import builtInActions from './BuiltInActions';
import { useTableColumns } from '../contexts/TableColumnContext';
import { CommandAction, BuiltInAction, useSpinner, useAuthContext } from '@terragotech/gen5-shared-components';
import useSubmitCommands, { CommandType } from './useSubmitCommand';
import WorkflowPageContainer, { AdditionalActions, Workflow } from '../containers/WorkflowPageContainer';
import { Column } from './tableHooks/useColumns';
import { ExecutionResult } from 'graphql';
import { AggregatesContextType, AssetType } from '../contexts/AggregatesContext/types';
import { has, isArray } from 'lodash';
import { useAlert } from '../contexts/AlertModalContext';
import { LanguageContext } from '../contexts/LanguageContext/languageContext';
import { useAggregates } from '../contexts/AggregatesContext';
import { useMutation } from '@apollo/client';
import { ADD_DATA_EXPORT_MESSAGE } from '../containers/DataExportTrackingContainer/graphql';
import { v4 } from 'uuid';
import { useRecoilValue } from 'recoil';
import { configState, quickSearchState, selectedRecordTypeFilterSelector } from '../recoil/atoms';
import { useHistory } from 'react-router-dom';
import { v4 as uuid4 } from 'uuid';

export interface ActionButton {
  label?: string;
  icon?: string;
  onClick: () => void | any;
}

interface ActionType {
  label?: string;
  icon?: string;
  action: BuiltInAction | CommandAction;
}
//TODO: allow this to take either a single assetId, or a function that returns an array. Don't allow the array to be passed as it's too slow.
interface ActionsExecutorContextProps {
  processAction: (
    action: ActionType,
    assets: AssetType[],
    assetId: string | string[] | (() => AssetType[]),
    onClick?: () => void,
    isCreationAction?: boolean,
    aggregateType?: string, // if not provided, current selection is used
    onFormSubmit?: () => void
  ) => ActionButton | null;
}

const defaultContext: ActionsExecutorContextProps = {
  processAction: () => ({ label: '', icon: '', onClick: () => {} }),
};

const ActionsExecutorContext = React.createContext<ActionsExecutorContextProps>(defaultContext);

const useActionsExecutor = () => {
  const context = useContext(ActionsExecutorContext);
  if (!context) {
    throw Error('useDialog must be within a UserProvider');
  }
  return context;
};

const processBuiltInAction = (
  baseAction: ActionType,
  columns: Column<AssetType>[],
  translate: (key: string, params?: Record<string, string | number | boolean>) => string,
  sortAssets: AggregatesContextType['sortAssets'],
  target?: AssetType | AssetType[] | (() => AssetType[]),
  onClickCallback?: () => void,
  toggleSpinner?: (loading: boolean) => void,
  defaultDateTimeFormat?: {
    dateFormatType?: string | undefined;
    dateFormat?: string | undefined;
    dateSeperator: string;
    timeFormat: string;
    isSwitch: boolean;
  },
  addToDataExports?: (inputData: {
    recordType: 'CSV' | 'PDF';
    recordCount: number;
    completionTime: number;
  }) => Promise<void>
) => {
  const actionName = (baseAction.action as BuiltInAction).actionName;
  const builtInAction = builtInActions[actionName];
  if (!builtInAction) {
    return null;
  }
  const onClick = async (newValues?: any) => {
    const startTime = Date.now();
    toggleSpinner && toggleSpinner(true);
    onClickCallback && onClickCallback();
    if (builtInAction && target) {
      const originalValues = typeof target === 'function' ? target() : target;
      const validAssets = newValues || originalValues;
      const sortedAssets = isArray(validAssets) ? sortAssets(validAssets) : validAssets;
      builtInAction.onClick(sortedAssets, columns, () => toggleSpinner && toggleSpinner(false), defaultDateTimeFormat, translate);
    }
    const endTime = Date.now();
    if (actionName === 'csvExport' || actionName === 'pdfExport') {
      const removeFunction = typeof target === 'function' ? target() : target;
      const updatedTarget = Array.isArray(removeFunction) ? removeFunction : [removeFunction];
      addToDataExports &&
        addToDataExports({
          recordType: actionName === 'csvExport' ? 'CSV' : 'PDF',
          recordCount: updatedTarget.length,
          completionTime: Number(endTime) - Number(startTime),
        });
    }
  };
  const label = baseAction.label || builtInAction.label;
  const icon = baseAction.icon || builtInAction.icon;
  return { label, icon, onClick };
};

interface ProcessCommandArgs {
  baseAction: ActionType;
  onClickCallback?: () => void;
  assetId: string | string[] | (() => AssetType[]);
  aggregateType: string;
  setWorkflow: (workflow: Workflow) => void;
  setAggregateType: (type: string) => void;
  setAggregateId: (id: string | string[]) => void;
  submitCommands: (commands: CommandType[]) => Promise<ExecutionResult<{}> | undefined>;
  setConfirmationTextAlertObj: (status: ProcessCommandArgs | undefined) => void;
  setIsRedirectToDetails?: (status: boolean) => void;
  onFormSubmit?: () => void;
  setAssets?: (assets: AssetType[]) => void;
  setAssetId?: (id: string | string[] | (() => AssetType[])) => void;
  setAdditionalActionButtons?: (actions: AdditionalActions) => void;
  assets?: AssetType[];
}

const showForm = (args: ProcessCommandArgs) => {
  args.setWorkflow({
    label: args.baseAction.label ?? '',
    action: args.baseAction.action as CommandAction,
    onFormSubmit: args.onFormSubmit,
  });
  let values = typeof args.assetId === 'function' ? args.assetId().map(agg => agg.id) : args.assetId;
  args.setAggregateId(values);
  args.setAggregateType(args.aggregateType);
};

const executeCommand = ({
  submitCommands,
  assetId,
  aggregateType,
  baseAction,
  setAggregateType,
  setAggregateId,
  setIsRedirectToDetails,
  onFormSubmit,
}: ProcessCommandArgs) => {
  const action = baseAction.action as CommandAction;
  const commands: CommandType[] = [];
  let values = typeof assetId === 'function' ? assetId().map(agg => agg.id) : assetId;
  const assetsIds = Array.isArray(values) ? values : [values];
  assetsIds.forEach(assetId => {
    commands.push({
      commandName: action.commandName,
      version: action.commandVersion,
      target: assetId,
      commandData: {},
      aggregateType: aggregateType,
      isSynchronous: action.isSynchronous,
      hasNoAggregateIdCommand: action.hasNoAggregateIdCommand,
    });
  });
  submitCommands(commands).then(() => {
    if (onFormSubmit) {
      onFormSubmit();
    }
    if (setIsRedirectToDetails && action.isRedirectToDetails && !Array.isArray(values)) {
      setAggregateId(values);
      setAggregateType(aggregateType);
      setIsRedirectToDetails(true);
    }
    //TODO: Update adminUpdate command to use graphQL response to update the data set rather than requery.
    // Temporarily disabling as no non-form commands require refreshing
    //setTimeout(() => {
    //assetInfo.refetchAll();
    //}, 500);
  });
};

const processCommand = (args: ProcessCommandArgs) => {
  const { baseAction, onClickCallback, setConfirmationTextAlertObj } = args;
  const action = baseAction.action as CommandAction;
  const onClick = (events?: Event, newValues?: Array<AssetType>) => {
    if (newValues && newValues.length > 0) {
      let ids = newValues.map(x => x.id);
      args.assetId = ids;
    }
    onClickCallback && onClickCallback();
    if (action.command) {
      showForm(args);
    } else {
      if (action?.confirmationText) {
        setConfirmationTextAlertObj(args);
      } else {
        executeCommand(args);
      }
    }

    if (
      action?.additionalActions &&
      args.setAssetId &&
      args.setAssets &&
      args.assets &&
      args.setAdditionalActionButtons
    ) {
      let values = typeof args.assetId === 'function' ? args.assetId().map(agg => agg.id) : args.assetId;
      args.setAssetId(values);
      args.setAssets(args.assets);
      args.setAdditionalActionButtons({
        confirmationText: action?.additionalActions?.confirmationText,
        buttons: [...action?.additionalActions?.buttons],
        actions: [...(action?.additionalActions?.actions || [])],
      });
    }
  };
  const label = baseAction.label;
  const icon = baseAction.icon;
  return { label, icon, onClick };
};

const ActionsExecutorProvider = (props: { children: React.ReactNode }) => {
  const [workflow, setWorkflow] = useState<Workflow | null>(null);
  const [aggregateId, setAggregateId] = useState<string | string[] | null>(null);
  const [isCreationAction, setIsCreationAction] = useState<boolean | undefined>(false);
  const [confirmationTextAlertObj, setConfirmationTextAlertObj] = useState<ProcessCommandArgs | undefined>();
  const [isRedirectToDetails, setIsRedirectToDetails] = useState<boolean>(false);
  const { selectedRecordTypeDefinition } = useRecordType();
  const [cmdAggregateType, setCmdAggregateType] = useState<string>(selectedRecordTypeDefinition?.name);
  const { submitCommands } = useSubmitCommands();
  const { columns } = useTableColumns();
  const { toggleSpinner } = useSpinner();
  const config = useRecoilValue(configState);
  const { defaultDateTimeFormat } = config;
  const { username } = useAuthContext();
  const { translate } = useContext(LanguageContext);
  const { openConfirmation } = useAlert();
  const { sortAssets } = useAggregates();
  const history = useHistory();
  const [addMessage] = useMutation(ADD_DATA_EXPORT_MESSAGE);
  const filterState = useRecoilValue(selectedRecordTypeFilterSelector);
  const quickSearch = useRecoilValue(quickSearchState);
  const [additionalActionButtons, setAdditionalActionButtons] = useState<AdditionalActions | null>(null);
  const [assets, setAssets] = useState<AssetType[]>([]);
  const [assetId, setAssetId] = useState<string | string[] | (() => AssetType[])>('');

  const openConfirmationDialog = useCallback(async () => {
    const status = await openConfirmation({
      title: `Confirm`,
      question: confirmationTextAlertObj && confirmationTextAlertObj.baseAction?.action?.confirmationText,
      confirmationText: 'Continue',
      type: 'confirmation',
    });
    if (status === 'confirm' && confirmationTextAlertObj) {
      executeCommand(confirmationTextAlertObj);
      setConfirmationTextAlertObj(undefined);
    } else {
      setConfirmationTextAlertObj(undefined);
    }
  }, [openConfirmation, executeCommand, setConfirmationTextAlertObj]);

  useEffect(() => {
    if (confirmationTextAlertObj) {
      openConfirmationDialog();
    }
  }, [confirmationTextAlertObj, openConfirmationDialog]);

  useEffect(() => {
    if (isRedirectToDetails) {
      history.push(`/${cmdAggregateType}/${aggregateId}/edit`);
      setIsRedirectToDetails(false);
    }
  }, [isRedirectToDetails, aggregateId, cmdAggregateType, history]);

  const handleAddExportMessage = useCallback(
    async (inputData: { recordType: 'CSV' | 'PDF'; recordCount: number; completionTime: number }) => {
      try {
        await addMessage({
          variables: {
            id: v4(),
            dateTime: new Date().toISOString(),
            user: username,
            exportType: inputData.recordType,
            recordType: selectedRecordTypeDefinition?.name ?? '',
            recordCount: inputData.recordCount,
            appliedFilters:
              Object.keys(filterState).length || quickSearch
                ? { ...filterState, globalSearch: quickSearch ? quickSearch : undefined }
                : null,
            completionTime: inputData.completionTime,
          },
        });
      } catch (error) {
        console.error('Error adding export message:', error);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [addMessage, username, cmdAggregateType, filterState]
  );

  const processAction = useCallback(
    (
      baseAction: ActionType,
      assets: AssetType[],
      assetId: string | string[] | (() => AssetType[]),
      onClickCallback?: () => void,
      isCreationAction?: boolean,
      aggregateType?: string,
      onFormSubmit?: () => void
    ) => {
      // Not sure how I feel about this use on __typename. Since we're asking for it specifically and it's part of the graphQL spec it should be ok though
      const _onClickCallback = () => {
        setIsCreationAction(isCreationAction);
        onClickCallback && onClickCallback();
      };

      if (
        baseAction?.action?.__typename === 'BuiltInAction' ||
        has(baseAction?.action, 'actionName' as keyof BuiltInAction)
      ) {
        // when given a function, pass it along as-is
        if (typeof assetId === 'function') {
          return processBuiltInAction(
            baseAction,
            columns,
            translate,
            sortAssets,
            assetId,
            _onClickCallback,
            toggleSpinner,
            defaultDateTimeFormat,
            handleAddExportMessage
          );
        }
        // if not a function, map the assetId we were given to the actual asset stored in assetInfo
        const assetsIds = Array.isArray(assetId) ? assetId : [assetId];
        const targets = assetsIds.map(assetId => assets.find(asset => asset.id === assetId)!).filter(asset => !!asset);
        return processBuiltInAction(
          baseAction,
          columns,
          translate,
          sortAssets,
          targets.length === 1 ? targets[0] : targets,
          _onClickCallback,
          toggleSpinner,
          defaultDateTimeFormat,
          handleAddExportMessage
        );
      } else if (
        baseAction?.action?.__typename === 'CommandReference' ||
        has(baseAction?.action, 'commandName' as keyof CommandAction)
      ) {
        if (username || baseAction.action.isPublic) {
          return processCommand({
            baseAction,
            assetId,
            submitCommands,
            aggregateType: aggregateType ?? selectedRecordTypeDefinition.name,
            onClickCallback: _onClickCallback,
            setWorkflow,
            setAggregateId,
            setAggregateType: setCmdAggregateType,
            setConfirmationTextAlertObj: setConfirmationTextAlertObj,
            setIsRedirectToDetails,
            onFormSubmit,
            setAdditionalActionButtons,
            setAssetId,
            setAssets,
            assets,
          });
        }
      }
      return null;
    },
    [columns, submitCommands, selectedRecordTypeDefinition.name, toggleSpinner, sortAssets, handleAddExportMessage]
  );

  const handleAdditionalActions = async () => {
    if (!additionalActionButtons || !additionalActionButtons.confirmationText) return;

    const isMultiSelectActive = Array.isArray(assetId);
    const additionalButtons = additionalActionButtons.buttons
      .filter(button => !isMultiSelectActive || button.type !== 'REDIRECT')
      .map(button => {
        return button.label;
      });
    if (additionalButtons.length === 0) return;

    const status = await openConfirmation({
      title: 'Workflow Complete - Next Steps',
      question: additionalActionButtons.confirmationText,
      confirmationText: 'Close',
      type: 'additionalActions',
      additionalButtons: additionalButtons,
    });
    if (status !== 'confirm') {
      const selectedButton = additionalActionButtons.buttons.find(button => button.label === status);
      if (!selectedButton) return;
      if (selectedButton.type === 'REDIRECT' && typeof assetId === 'string') {
        history.push(`/${cmdAggregateType}/${assetId}/edit`);
        return;
      }

      const selectedAction = additionalActionButtons.actions?.find(
        atn =>
          atn.aggregateName === selectedButton.aggregateName &&
          atn.action.commandName === selectedButton.commandName &&
          atn.action.commandVersion === selectedButton.commandVersion
      );
      if (selectedAction?.action) {
        const newAction = {
          label: selectedButton.label,
          action: {
            ...selectedAction.action,
            ...(selectedAction.action?.additionalActions && {
              additionalActions: {
                confirmationText: selectedAction.action.additionalActions.confirmationText,
                buttons: [...selectedAction.action.additionalActions?.buttons],
                actions: [...(additionalActionButtons?.actions || [])],
              },
            }),
          },
        };

        const isCreationCommand = newAction.action?.isCreationCommand || false;
        processAction(
          newAction,
          isCreationCommand ? [] : assets,
          isCreationCommand ? uuid4() : assetId,
          () => {},
          isCreationCommand,
          selectedAction.aggregateName
        )?.onClick();
        return;
      }
      return;
    }
  };

  return (
    <ActionsExecutorContext.Provider
      value={{
        processAction,
      }}>
      {props.children}
      {workflow && aggregateId && (
        <WorkflowPageContainer
          aggregateId={aggregateId}
          aggregateType={cmdAggregateType}
          workflow={workflow}
          creationAction={isCreationAction}
          onDonePress={() => {
            setWorkflow(null);
            if (workflow.onFormSubmit) workflow.onFormSubmit();
            handleAdditionalActions();
            setAdditionalActionButtons(null);
            // need to be done only when the workflow submitted I guess and when we leave this dialog
            // TODO - refetch data only when leaving the AssetDataEditor if changes was made or not?
            // setTimeout(() => {
            //   assetInfo.refetchAll();
            // }, 500);
          }}
          onCancelPress={() => {
            setWorkflow(null);
            setAggregateId(null);
          }}
        />
      )}
    </ActionsExecutorContext.Provider>
  );
};

export { ActionsExecutorProvider, useActionsExecutor };
